import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import PageFade from '@/components/feature/PageFade';
import { readHomeReturn, saveHomeReturn } from '@/utils/homeReturnStore';

function BackButton({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 h-[48px] px-4 text-[14px] font-medium text-solar-ink hover:text-solar-green transition-colors cursor-pointer whitespace-nowrap bg-transparent border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-solar-green focus-visible:ring-offset-2 rounded-md"
    >
      <i className="ri-arrow-left-line"></i>
      {label}
    </button>
  );
}

export default function Applications() {
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    const saved = readHomeReturn();
    if (!saved) saveHomeReturn(0, 'projects');
    navigate('/');
  }, [navigate]);

  const items = [
    {
      tag: 'Residential',
      copy: 'Solar for the home you live in.',
      img: 'https://readdy.ai/api/search-image?query=modern%20contemporary%20two%20story%20family%20home%20exterior%20with%20cleanly%20integrated%20black%20rooftop%20solar%20panels%2C%20warm%20wood%20siding%20and%20white%20stucco%2C%20soft%20green%20lawn%20and%20mature%20trees%2C%20soft%20natural%20morning%20daylight%2C%20clear%20sky%2C%20editorial%20architecture%20photography%2C%20calm%20quiet%20premium%20feeling%2C%20cool%20neutral%20tones&width=800&height=1000&seq=app-res-01&orientation=portrait',
    },
    {
      tag: 'Commercial',
      copy: 'Energy designed around how business operates.',
      img: 'https://readdy.ai/api/search-image?query=modern%20contemporary%20low%20rise%20commercial%20office%20building%20with%20cleanly%20integrated%20rooftop%20solar%20panel%20array%2C%20light%20facade%20with%20warm%20wood%20accents%2C%20subtle%20landscaping%2C%20bright%20natural%20daylight%2C%20clear%20sky%2C%20editorial%20architecture%20photography%2C%20calm%20premium%20quiet%20composition%2C%20cool%20neutral%20tones&width=800&height=1000&seq=app-com-01&orientation=portrait',
    },
    {
      tag: 'Hospitality',
      copy: 'Clean energy integrated into places people stay and gather.',
      img: 'https://readdy.ai/api/search-image?query=modern%20boutique%20resort%20hotel%20building%20with%20cleanly%20integrated%20rooftop%20and%20canopy%20solar%20panels%2C%20warm%20wood%20and%20stone%20architecture%2C%20lush%20coastal%20landscaping%2C%20soft%20golden%20hour%20daylight%2C%20editorial%20architecture%20photography%2C%20calm%20quiet%20premium%20composition%2C%20warm%20neutral%20tones&width=800&height=1000&seq=app-hos-01&orientation=portrait',
    },
  ];

  return (
    <PageFade>
    <div className="min-h-screen bg-solar-bg text-solar-ink">
      <div className="sticky top-0 z-10 bg-solar-bg/95 backdrop-blur-md border-b border-solar-divider">
        <div className="container-x flex items-center justify-between h-16">
          <BackButton onClick={goBack} />
          <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Applications</p>
        </div>
      </div>

      <main>
        <section className="pt-[56px] md:pt-[72px] pb-[40px] md:pb-[56px] bg-solar-bg">
          <div className="container-x">
            <div className="max-w-[720px]">
              <h1 className="display-font text-[32px] md:text-[48px] lg:text-[54px] font-semibold leading-[1.05] text-solar-ink tracking-tight">
                Built around real places.
              </h1>
              <p className="mt-5 max-w-[520px] text-solar-muted text-[15px] md:text-[16px] leading-relaxed">
                Solar designed around the way homes, businesses, and destinations actually operate.
              </p>
            </div>
          </div>
        </section>

        <section className="pb-[96px] md:pb-[128px] bg-solar-bg">
          <div className="container-x">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {items.map((it) => (
                <div key={it.tag} className="block">
                  <div className="aspect-[4/3] md:aspect-[4/5] w-full overflow-hidden rounded-2xl bg-solar-divider">
                    <img
                      src={it.img}
                      alt={it.tag}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-top transition-[transform,filter] duration-700 hover:scale-[1.01] hover:brightness-[1.02]"
                    />
                  </div>
                  <div className="mt-5">
                    <p className="text-xs font-medium text-solar-muted tracking-wider uppercase">{it.tag}</p>
                    <h3 className="display-font mt-2 text-[20px] md:text-[22px] font-semibold text-solar-ink leading-tight max-w-[320px]">
                      {it.copy}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </PageFade>
  );
}